import {
  forEach,
  isPlainObject,
  isArray,
  transform,
  reduce,
  omitBy,
  isUndefined,
  isNil,
  pick,
  get,
  isEqual,
  isEmpty
} from 'lodash'
import {
  isImageItem,
  isVideoItem,
  isSoundAssetItem,
  isImageHolderItem,
  isTextItem,
  isColorableItem
} from 'powtoon-document'
import {KEYFRAME_PROPS} from 'powtoon-document/consts/animationGraphs'
import {isValidLink, isValidDynamicLink} from 'powtoon-commons/utils/strings'
import {decimalOfInteger, isDecimalInteger, roundToPrecision} from 'powtoon-commons/utils/numbers'
import {
  getFirstSprite,
  getLipSyncPlaybackSettingsFromTrack,
  isLipSyncAudioSpriteActiveInTrack,
  getLipSyncAudioTrackFromTrack
} from 'powtoon-document/utils/trackSpriteUtils'
import {isCameraValid} from 'powtoon-commons/utils/cameraAnimationGraphs'
import {isMasterTrackDuplicateExists} from 'powtoon-commons/models/utils/masterObject'
import {colord, getFormat} from 'colord'
import {isRichTextData, isDocText, isRichText, isValidTextSpriteStyle} from 'powtoon-commons/richText/utils'
import {percentageSizeToPixelSize} from 'powtoon-commons/utils'

function extractManifestReferences(obj, manifestReferences) {
  if (!obj) {
    return
  }

  const {contentItemId, sceneId, assetType, itemType, fontFamily, featureId} = obj
  if (contentItemId || sceneId) {
    manifestReferences.push({contentItemId, sceneId, assetType, itemType, fontFamily})
  }
  if (featureId) {
    // we can have (contentItemId / sceneId) + featureId on same document element
    // each 1 represent different manifest item
    manifestReferences.push({featureId})
  }
  forEach(obj, (value) => {
    if (isPlainObject(value) || isArray(value)) {
      extractManifestReferences(value, manifestReferences)
    }
  })
}

function getAllManifestReferences(powtoon) {
  const manifestReferences = []
  if (powtoon.templateId){
    manifestReferences.push({templateId: powtoon.templateId})
  }
  extractManifestReferences(powtoon.slides, manifestReferences)
  extractManifestReferences(powtoon.audioTracks, manifestReferences)
  extractManifestReferences(powtoon.masters, manifestReferences)
  return manifestReferences
}

function findInManifest(reference, manifest) {
  const {contentItemId, sceneId, templateId, featureId} = reference
  const manifestItemId = featureId || contentItemId || sceneId || templateId
  return (
    manifest.assets[manifestItemId] ||
    manifest.images[manifestItemId] ||
    manifest.sounds[manifestItemId] ||
    manifest.fonts[manifestItemId] ||
    (manifest.videos && manifest.videos[manifestItemId])
  )
}

export function validateIdsUniqness(powtoon) {
  const usedKeys = {}
  const errors = []

  powtoon.slides.forEach(slide => {
    usedKeys[slide.id] = (usedKeys[slide.id] || 0) + 1

    slide.tracks.forEach(track => {
      usedKeys[track.id] = (usedKeys[track.id] || 0) + 1

      track.sprites.forEach(sprite => {
        usedKeys[sprite.id] = (usedKeys[sprite.id] || 0) + 1
      })
    })
  })

  Object.keys(usedKeys).forEach(key => {
    if (usedKeys[key] > 1) {
      errors.push({
        message: 'Duplicated ids inside document',
        dataPath: 'non-uniq-id',
        params: {key, usedKeys: usedKeys[key]}
      })
    }
  })

  return errors
}

function buildDataPath({slideNum, trackNum, hasSprite = false, extraPath = ''} = {}) {
  const slidePath = slideNum !== undefined ? `.slides[${slideNum}]` : ''
  const trackPath = trackNum !== undefined ? `.tracks[${trackNum}]` : ''
  const spritePath = hasSprite ? '.sprites[0]' : ''
  return `${slidePath}${trackPath}${spritePath}${extraPath}`
}

export function validateManifestReferences(references, manifest) {
  const errors = transform(references, (errors, reference) => {
    if (reference?.itemType === 'text') {
      // TODO need to support backward compatibility for unit tests
      // since this function is run for all versions of document but should run only for latest version
      const fontId = reference.fontFamily || reference.contentItemId
      const manifestItem = manifest.fonts[fontId]
      if (!manifestItem) {
        errors.push({
          message: 'Font is missing from manifest.',
          dataPath: buildDataPath({extraPath: `.manifest.fonts['${fontId}']`}),
          params: {reference}
        })
      }
    }
    else {
      const manifestItem = findInManifest(reference, manifest)
      if (!manifestItem) {
        errors.push({
          message: 'Item"s manifest descriptor is missing',
          dataPath: 'ManifestDescriptorMissing',
          params: {reference: omitBy(reference, isUndefined)}
        })
      }
      else if (reference.contentItemId){
        if (reference.itemType !== manifestItem.itemType) {
          errors.push({
            message: 'DocItem and ManifestItem have different itemTypes.',
            dataPath: 'ManifestHasDifferentItemTypes',
            params: {reference, manifestItem}
          })
        }
        if (manifestItem.assetType && reference.assetType !== manifestItem.assetType) {
          errors.push({
            message: 'DocItem and ManifestItem have different assetTypes.',
            dataPath: 'ManifestHasDifferentAssetTypes',
            params: {reference, manifestItem}
          })
        }
      }
    }
  }, [])

  return errors
}

export function validateManifestContainsAllDocumentObjects(powtoon) {
  const manifestReferences = getAllManifestReferences(powtoon)
  return validateManifestReferences(manifestReferences, powtoon.manifest)
}

export function validateSpriteContent(powtoon) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    transform(slide.tracks, (errors, track, trackNum) => {
      const sprite = getFirstSprite(track)
      const spriteContent = sprite.content
      if (!isImageHolderItem(sprite)) {
        return
      }

      if (isEmpty(spriteContent)) {
        return
      }

      if (!spriteContent.id) {
        errors.push({
          message: 'Sprite.content.id doesn\'t exist.',
          dataPath: buildDataPath({slideNum, trackNum, hasSprite: true}),
          params: {spriteContent: sprite.content}
        })
      }
      const {width, height} = percentageSizeToPixelSize(spriteContent, sprite)
      const manifestItem = findInManifest(spriteContent, powtoon.manifest)
      if (manifestItem) {
        const {originalWidth, originalHeight} =
        (isVideoItem(spriteContent) ? manifestItem.videoData : manifestItem) || {}
        const currentAspectRatio = width / height
        const expectedAspectRatio = originalWidth / originalHeight
        const isDifferentAspectRatio = expectedAspectRatio && currentAspectRatio &&
            (roundToPrecision(currentAspectRatio - expectedAspectRatio, 1) !== 0)

        if (isDifferentAspectRatio) {
          errors.push({
            message: 'Sprite content has incorrect aspect ratio',
            dataPath: buildDataPath({slideNum, trackNum, hasSprite: true}),
            params: {
              spriteContent: sprite.content,
              currentAspectRatio: roundToPrecision(currentAspectRatio, 1),
              expectedAspectRatio: roundToPrecision(expectedAspectRatio, 1)
            }
          })
        }
      }
    }, errors)
  }, [])
}

export function validateSlideTracksDuration(powtoon, {isValidTime, getMinTimeStep}) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    const {duration: slideDuration} = slide
    transform(slide.tracks, (errors, track, trackNum) => {
      if (track.startTime < 0) {
        /**
         * The ajv schema validation should catch cases where startTime < 0 as well
         * We catch it here as well ONLY for a better error reporting.
         */
        errors.push({
          message: 'Track.startTime < 0.',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {trackStartTime: track.startTime, slideDuration}
        })
      }

      if (track.endTime > slideDuration) {
        errors.push({
          message: 'Track.endTime > slideDuration.',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {trackEndTime: track.endTime, slideDuration}
        })
      }

      if (track.startTime > track.endTime) {
        // eslint-disable-next-line max-len
        errors.push({
          message: 'Track.startTime > Track.endTime',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {trackEndTime: track.endTime, trackStartTime: track.startTime}
        })
      }


      if (!isValidTime(track.startTime)) {
        errors.push({
          message: 'Track.startTime is not valid',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {trackStartTime: track.startTime, slideDuration}
        })
      }
      if (!isValidTime(track.endTime)) {
        errors.push({
          message: 'Track.endTime is not valid',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {trackEndTime: track.endTime, slideDuration}
        })
      }

      const firstSprite = getFirstSprite(track)
      if (isVideoItem(firstSprite)) {
        const {playbackSettings} = firstSprite
        const trackDuration = track.endTime - track.startTime
        const playbackSettingsDuration = playbackSettings.endTime - playbackSettings.startTime
        const differenceDuration = trackDuration - playbackSettingsDuration
        if (differenceDuration > getMinTimeStep() ) {
          errors.push({
            message: 'VideoTrack duration is bigger than playbackSettingsDuration',
            dataPath: buildDataPath({slideNum, trackNum, hasSprite: true}),
            params: {
              trackStartTime: track.startTime,
              trackEndTime: track.endTime,
              playbackSettings
            }
          })
        }

        if (differenceDuration < 0) {
          errors.push({
            message: 'VideoTrack duration is smaller than playbackSettingsDuration',
            dataPath: buildDataPath({slideNum, trackNum, hasSprite: true}),
            params: {
              trackStartTime: track.startTime,
              trackEndTime: track.endTime,
              playbackSettings
            }
          })
        }
      }
      else if (isLipSyncAudioSpriteActiveInTrack(track)) {
        const audioTrack = getLipSyncAudioTrackFromTrack(track)
        const playbackSettings = getLipSyncPlaybackSettingsFromTrack(track)
        if (audioTrack.startTime
          || playbackSettings?.startTime !== 0
          || playbackSettings?.endTime < 0) {
          errors.push({
            message: 'LipSyncAudioTrack has invalid playbackSettings duration',
            dataPath: buildDataPath({slideNum, trackNum, hasSprite: true}),
            params: {
              trackStartTime: track.startTime,
              trackEndTime: track.endTime,
              lipSyncAudioTrackStartTime: audioTrack.startTime,
              lipSyncAudioTrackEndTime: audioTrack.endTime,
              lipSyncAudioTrackPlaybackSettingsStartTime: playbackSettings?.startTime,
              lipSyncAudioTrackPlaybackSettingsEndTime: playbackSettings?.endTime
            }
          })
        }
      }
    }, errors)
  }, [])
}

export function validateSlideTracksLink(powtoon) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    transform(slide.tracks, (errors, {link}, trackNum) => {
      if (!isValidLink(link) && !isValidDynamicLink(link)) {
        errors.push({
          message: 'Track link is not valid.',
          dataPath: buildDataPath({slideNum, trackNum}),
          params: {link}
        })
      }
    }, errors)
  }, [])
}

export function validateManifestAssetVersionsExist({manifest}) {
  const errors = []

  transform(manifest.assets, (errors, asset, assetId) => {
    if (isImageItem(asset) && asset.imageData===null) { // we allow imageData to be undefined, but not null
      errors.push({
        message: 'Manifest image asset is missing required imageData',
        dataPath: buildDataPath({extraPath: `.manifest.assets["${assetId}"]`}),
        params: {}
      })
    }
    if (isVideoItem(asset) && isNil(asset.videoData)) { // we must have videoData
      errors.push({
        message: 'Manifest video asset is missing required videoData',
        dataPath: buildDataPath({extraPath: `.manifest.assets["${assetId}"]`}),
        params: {}
      })
    }
  }, errors)

  transform(manifest.sounds, (errors, sound, soundId) => {
    if (isSoundAssetItem(sound) && sound.audioData===null) { // we allow audioData to be undefined, but not null
      errors.push({
        message: 'Manifest sound asset is missing required audioData',
        dataPath: buildDataPath({extraPath: `.manifest.sounds["${soundId}"]`}),
        params: {}
      })
    }
  }, errors)

  return errors
}

export function validateSlideTracksAnimationGraphs(powtoon, {isValidTime}) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    transform(slide.tracks, (errors, track, trackNum) => {
      if ('animationGraphs' in track) {
        const firstKeyframeValues = get(track, 'animationGraphs.keyframes[0].values')
        const spriteValues = pick(track.sprites[0], KEYFRAME_PROPS)
        if (!isEqual(spriteValues, firstKeyframeValues)) {
          errors.push({
            message: 'First keyframe values does not match sprite',
            dataPath: buildDataPath({slideNum, trackNum}),
            params: {
              keyframeValues: firstKeyframeValues,
              spriteValues
            }
          })
        }

        const firstKeyframeTime = get(track, 'animationGraphs.keyframes[0].time')
        const secondKeyframeTime = get(track, 'animationGraphs.keyframes[1].time')
        const firstKeyframeTimeValid = isValidTime(firstKeyframeTime)
        const secondKeyframeTimeValid = isValidTime(secondKeyframeTime)
        if (!firstKeyframeTimeValid || !secondKeyframeTimeValid) {
          errors.push({
            message: 'Keyframe time is not valid',
            dataPath: buildDataPath({slideNum, trackNum}),
            params: {
              firstKeyframeTime, secondKeyframeTime,
              firstKeyframeTimeValid, secondKeyframeTimeValid
            }
          })
        }
      }
    }, errors)
  }, [])
}

function validateSlideTracksSpriteColors(powtoon) {
  // https://github.com/YousefED/typescript-json-schema/issues/514
  // current code state doesn't validate pattern for string[] in our currentSchema
  // until the above will be fixed ,we will use this code to validate "Colorable" colors array
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    transform(slide.tracks, (errors, track, trackNum) => {
      const firstSprite = getFirstSprite(track)
      if (isColorableItem(firstSprite) &&  !isEmpty(firstSprite.colors) ) {
        firstSprite.colors.forEach((color, colorIndex) => {
          const isValid = colord(color).isValid()
          const format = getFormat(color)
          if (!(isValid && format === 'hex')) {
            errors.push({
              message: 'Track sprite colors has wrong format',
              dataPath: buildDataPath({slideNum, trackNum}),
              params: {
                format, colorIndex, color
              }
            })
          }
        })
      }
    }, errors)
  }, [])
}

export function validateSlideCamera(powtoon, {isValidTime}) {
  return transform(powtoon.slides, (errors, {duration, camera}, slideNum) => {
    if (!isCameraValid(camera, duration, isValidTime)) {
      errors.push({
        message: 'Slide contains not-valid camera',
        dataPath: buildDataPath({slideNum, extraPath: '.camera'}),
        params: {
          camera,
          slideDuration: duration
        }
      })
    }
  }, [])
}

export function validateMasterTracks(powtoon) {
  const masterTracks = powtoon.masters?.tracks
  return transform(masterTracks, (errors, specificTracks, tracksPlaceKey) => {
    specificTracks.some((track, index) => {
      if (isMasterTrackDuplicateExists(track, masterTracks)) {
        errors.push({
          message: 'Master tracks duplicates is detected',
          dataPath: buildDataPath({
            extraPath: `.masters.tracks.${tracksPlaceKey}[${index}]`
          })
        })
        return true
      }
      return false
    })
  }, [])
}

export function validateSlideVideoBackgroundTime(powtoon) {
  const errors = transform(powtoon.slides, (errors, slide, slideNum) => {
    const {duration, background: {itemType, playbackSettings} = {}} = slide
    if (itemType === 'video' && playbackSettings) {
      const slideVideoDuration = playbackSettings.endTime - playbackSettings.startTime
      if (slideVideoDuration < 0) {
        errors.push({
          message: 'VideoBackground invalid playback settings',
          dataPath: buildDataPath({slideNum, extraPath: '.background'}),
          params: {
            playbackSettings
          }
        })
      }
      if (slideVideoDuration > duration) {
        errors.push({
          message: 'VideoBackground duration longer than slide',
          dataPath: buildDataPath({slideNum, extraPath: '.background'}),
          params: {
            playbackSettings, slideDuration: duration
          }
        })
      }
    }
  }, [])
  return errors
}

export function validateSlideAudioTracksTime(powtoon, {isValidTime}) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    const {duration: slideDuration} = slide
    transform(slide.audioTracks, (errors, {endTime, startTime, playbackSettings}, trackNum) => {
      const extraPath = `.audioTracks[${trackNum}]`
      if (startTime < 0 || !isValidTime(startTime)) {
        errors.push({
          message: 'SlideAudioTrack startTime is not valid',
          dataPath: buildDataPath({slideNum, extraPath}),
          params: {
            startTime, slideDuration
          }
        })
      }

      if (endTime > slideDuration || !isValidTime(endTime)) {
        errors.push({
          message: 'SlideAudioTrack endTime is not valid',
          dataPath: buildDataPath({slideNum, extraPath}),
          params: {
            endTime, slideDuration
          }
        })
      }

      if (playbackSettings.endTime <= playbackSettings.startTime) {
        errors.push({
          message: 'SlideAudioTrack playbackSettings is not valid',
          dataPath: buildDataPath({slideNum, extraPath}),
          params: {
            playbackSettings
          }
        })
      }
      if ((playbackSettings.endTime - playbackSettings.startTime) > (endTime - startTime)) {
        errors.push({
          message: 'SlideAudioTrack and playbackSettings duration are not valid',
          dataPath: buildDataPath({slideNum, extraPath}),
          params: {
            playbackSettings, endTime, startTime
          }
        })
      }
    }, errors)
  }, [])
}

export function validateTextSprites(powtoon) {
  return transform(powtoon.slides, (errors, slide, slideNum) => {
    transform(slide.tracks, (errors, track, trackNum) => {
      const {sprites: [sprite]} = track
      if (isTextItem(sprite)) {
        const {contentItemId, fontName} = sprite

        if (!fontName) {
          // validate Text Font Name
          errors.push({
            message: 'FontName is missing',
            dataPath: buildDataPath({slideNum, trackNum}),
            params: {
              track
            }
          })
        }
        if (!contentItemId) {
          // validate Text ContentItemId
          errors.push({
            message: 'ContentItemId is missing.',
            dataPath: buildDataPath({slideNum, trackNum}),
            params: {track}
          })
        }
        
        if (isDocText(sprite) && isRichTextData(sprite.richText)) {
          const value = sprite.richText.value
          if ( value && Array.isArray(value)) {
            value.forEach((val) => {
              // key is either an array index or object key
              
              if (isRichText(val)) {
                if (val.children && val.children.length === 0) {
                  errors.push({
                    message: 'richText value array , item should not be with empty "children" array ',
                    dataPath: buildDataPath({slideNum, trackNum}),
                    params: {val}
                  })
                }
                else if (!isValidTextSpriteStyle(sprite)) {
                  errors.push({
                    message: 'text sprite and richText have different text style',
                    dataPath: buildDataPath({slideNum, trackNum}),
                    params: {sprite}
                  })
                }
              }
            })
          }
        }
      }

    }, errors)
  }, [])
}

const formatErrors = errors => {
  return reduce(errors, (errorMsg, error) => {
    return [
      errorMsg ? `${errorMsg}\n` : '',
      error.message,
      error.dataPath ?? '',
      JSON.stringify(error.params) ?? ''
    ].filter(i => !!i).join('\n')
  }, '')
}

export const validate = (powtoon, options) => {
  const validateOptions = {
    strictMode: false,
    ignoreManifest: false,
    isValidTime: isDecimalInteger,
    getMinTimeStep: () => decimalOfInteger,
    ignoreRichText: true,
    ...omitBy(
      options,
      isNil
    )
  }
  const errors = []

  Array.prototype.push.apply(errors, validateIdsUniqness(powtoon))
  Array.prototype.push.apply(errors, validateSlideTracksDuration(powtoon, validateOptions))
  Array.prototype.push.apply(errors, validateSlideTracksAnimationGraphs(powtoon, validateOptions))
  Array.prototype.push.apply(errors, validateSlideVideoBackgroundTime(powtoon))
  Array.prototype.push.apply(errors, validateSlideTracksLink(powtoon))
  Array.prototype.push.apply(errors, validateSlideAudioTracksTime(powtoon, validateOptions))
  Array.prototype.push.apply(errors, validateSpriteContent(powtoon))
  Array.prototype.push.apply(errors, validateSlideCamera(powtoon, validateOptions))
  Array.prototype.push.apply(errors, validateMasterTracks(powtoon))
  Array.prototype.push.apply(errors, validateSlideTracksSpriteColors(powtoon))
  if (!validateOptions.ignoreRichText){
    Array.prototype.push.apply(errors, validateTextSprites(powtoon))
  }

  if (!validateOptions.ignoreManifest) {
    Array.prototype.push.apply(errors, validateManifestContainsAllDocumentObjects(powtoon))
  }

  if (validateOptions.strictMode) {
    Array.prototype.push.apply(errors, validateManifestAssetVersionsExist(powtoon))
  }

  // Output format:
  // errors: [{dataPath, message, params}, ...]
  // errorMessage: string
  return errors.length ? {errors, errorMessage: `Total errors: #${errors.length}:\n\n${formatErrors(errors)}`} : null
}
